import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {GoogleMap, Marker, useJsApiLoader} from '@react-google-maps/api';
import {Alert, Button, Space, Spin} from 'antd';
import {useLazyQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  GET_ZIPCODE_MAP_BOUNDARY,
  GetZipcodeMapBoundaryResponse,
} from '../../pages/wms/zipcode_map_geofence/graphql/queries/getZipcodeMapBoundary';
import {
  GET_POD_EXEMPT_ACTIVE_FENCES,
  PodExemptActiveFencesResponse,
} from '../../graphql/podExempt/queries';
import {PodExemptPointInput} from '../../graphql/podExempt/types';

// 与 zipcode_map_geofence 等页面同源的内联 key（项目惯例：页面内自持）
const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

const MAP_CONTAINER_STYLE = {width: '100%', height: '420px'};
const US_CENTER = {lat: 39.5, lng: -98.35};
const MAX_POINTS = 100;

type LatLng = google.maps.LatLngLiteral;

/** boundary 字段为 JSON（简单环 [{lat,lng}] 或带洞 [[{lat,lng}],...]），照 ZipcodeMapPage 解析口径 */
function parseBoundaryPaths(raw: string): LatLng[] | null {
  try {
    const coords = JSON.parse(raw);
    const rawPath = Array.isArray(coords?.[0]) ? coords[0] : coords;
    if (!Array.isArray(rawPath)) {
      return null;
    }
    const sanitized = rawPath
      .filter(
        (p: unknown): p is LatLng =>
          typeof (p as LatLng)?.lat === 'number' && typeof (p as LatLng)?.lng === 'number',
      )
      .map((p: LatLng) => ({lat: p.lat, lng: p.lng}));
    return sanitized.length >= 3 ? sanitized : null;
  } catch {
    return null;
  }
}

interface Props {
  /** 当前选中仓 id（圈画模式叠加围栏的查询参数）；只读回显可不传 */
  warehouseId?: string | number | null;
  /** 已选 zipcode，变更时重新加载边界并清空已画点 */
  zipcode: string | null;
  points: PodExemptPointInput[];
  onPointsChange: (points: PodExemptPointInput[]) => void;
  /** 只读回显模式（详情页）：不可编辑 */
  readonly?: boolean;
  /** 附加标记（500m 参考等）：{lat, lng, label} */
  extraMarkers?: {lat: number; lng: number; label?: string}[];
  /** 外部注入的叠加围栏（审核页：同邮编活跃围栏由调用方按审核端接口拉取）；传入后组件不再自查 */
  activeFences?: {status: string; pointsJson: string | null}[];
  /** 截断预览（青色实线）：校验返回的「截断后生效形状」，与本次圈画叠加对比 */
  previewPoints?: PodExemptPointInput[] | null;
}

/**
 * 围栏圈画地图：加载 zipcode 边界（1 次查询页内缓存）→ 地图点选顶点 → 组装 points。
 * 圈画过程零请求；重叠/越界由提交时服务端校验兜底，前端只做基础点数提示。
 */
const PodExemptFenceDrawMap: React.FC<Props> = ({
  warehouseId,
  zipcode,
  points,
  onPointsChange,
  readonly = false,
  extraMarkers,
  activeFences,
  previewPoints,
}) => {
  const {t} = useTranslation();
  const {isLoaded, loadError} = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // 由 hook data 驱动而非 execute promise：缓存命中时 promise 可能不回吐 data（Apollo 已知行为），
  // 曾导致抽屉二次打开边界/围栏多边形消失
  const [fetchBoundary, {data: boundaryData, loading: boundaryLoading}] =
    useLazyQuery<GetZipcodeMapBoundaryResponse>(GET_ZIPCODE_MAP_BOUNDARY, {fetchPolicy: 'cache-first'});

  const [fetchActiveFences, {data: activeFencesData}] =
    // 活跃围栏随上报实时变化，不能 cache-first（曾致新建围栏后重开页面读到缓存空列表）
    useLazyQuery<PodExemptActiveFencesResponse>(GET_POD_EXEMPT_ACTIVE_FENCES, {fetchPolicy: 'network-only'});

  useEffect(() => {
    if (zipcode) {
      fetchBoundary({variables: {zipcode}});
      if (activeFences == null && !readonly && warehouseId != null) {
        // 圈画模式叠加同邮编活跃围栏（D8：与其中任何一个相交提交都会被拦，先看见再画）
        fetchActiveFences({variables: {warehouseId, zipcode}});
      }
    }
  }, [zipcode, readonly, warehouseId, activeFences, fetchBoundary, fetchActiveFences]);

  /** 叠加围栏：生效=绿色 / 待审=橙色（对齐原型图例），驳回/关闭/下线的不查不显示。
      审核页由调用方经审核端接口注入 activeFences（DSP 端接口有本 DSP 数据域限制，审核角色调不通） */
  const overlayFences = useMemo(() => {
    const source = activeFences ?? (readonly || !zipcode ? [] : activeFencesData?.podExemptActiveFences ?? []);
    return source
      .map((fence) => {
        try {
          const parsed = JSON.parse(fence.pointsJson ?? '[]') as PodExemptPointInput[];
          const path = (Array.isArray(parsed) ? parsed : [])
            .filter((p) => typeof p?.latitude === 'number' && typeof p?.longitude === 'number')
            .map((p) => ({lat: p.latitude, lng: p.longitude}));
          return path.length >= 3 ? {status: fence.status, path} : null;
        } catch {
          return null;
        }
      })
      .filter((f): f is {status: string; path: LatLng[]} => f !== null);
  }, [activeFences, readonly, zipcode, activeFencesData]);

  const boundaryPaths: LatLng[][] = useMemo(() => {
    if (!zipcode) {
      return [];
    }
    return (boundaryData?.getZipcodeMapBoundary ?? [])
      .map((boundary) => parseBoundaryPaths(boundary.boundary))
      .filter((path): path is LatLng[] => path !== null);
  }, [zipcode, boundaryData]);

  const drawnPath: LatLng[] = useMemo(
    () => points.map((p) => ({lat: p.latitude, lng: p.longitude})),
    [points],
  );

  // 初始取景只做一次：不能依赖落点重取景，否则绘制中每点一下视野就被拉回。
  // key 绑「zipcode + 边界数据指纹」而非只绑 zipcode——切邮编时可能出现「zipcode 已变、
  // boundaryData 还是旧的」的中间渲染，只绑 zipcode 会把取景标记提前占掉，真边界到达后永不取景
  const fittedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    fittedKeyRef.current = null; // 地图实例重建后允许重新取景
  }, [map]);
  const fitAll = useCallback(() => {
    if (!map || (boundaryPaths.length === 0 && drawnPath.length === 0)) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    boundaryPaths.flat().forEach((point) => bounds.extend(point));
    drawnPath.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
  }, [map, boundaryPaths, drawnPath]);
  useEffect(() => {
    if (!map || (boundaryPaths.length === 0 && drawnPath.length === 0)) {
      return;
    }
    const first = boundaryPaths[0]?.[0];
    const fitKey = `${zipcode ?? ''}|${first ? `${first.lat},${first.lng}` : 'none'}`;
    if (fittedKeyRef.current === fitKey) {
      return;
    }
    fittedKeyRef.current = fitKey;
    fitAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, boundaryPaths, zipcode]);

  // 抽屉/弹层内容器初始宽度可能未就绪（实测底图按 24px 宽请求变灰块），尺寸变化时通知地图重排并重新取景。
  // 回调经 ref 取最新数据（直接闭包会拿到注册时的陈旧 boundary/drawn），observer 只随 map 建一次
  const fitAllRef = useRef(fitAll);
  fitAllRef.current = fitAll;
  useEffect(() => {
    if (!map) {
      return undefined;
    }
    const container = map.getDiv();
    let lastWidth = container.clientWidth;
    const observer = new ResizeObserver(() => {
      google.maps.event.trigger(map, 'resize');
      // 只在容器从「初始未就绪的极小宽度」长到正常宽度的那一次 refit（灰块修复场景）；
      // 之后的任何尺寸变化都不再取景，避免打扰用户手动缩放
      const width = container.clientWidth;
      const grewFromTiny = lastWidth < 200 && width >= 200;
      lastWidth = width;
      if (grewFromTiny) {
        fitAllRef.current();
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  // 多边形命令式自管：@react-google-maps/api 的声明式 <Polygon> 在抽屉销毁重建地图后不落图
  // （实测 paths 数据正常但图上无多边形），改为直接持有 google.maps.Polygon 实例。
  // 三类 overlay 拆三个 effect 各管各的：合在一个 effect 里时每落一个点会把邮编边界和
  // 全部活跃围栏也销毁重建（点越多闪烁越明显）

  /** 邮编边界（蓝色轮廓） */
  const boundaryOverlaysRef = useRef<google.maps.Polygon[]>([]);
  useEffect(() => {
    if (!map) {
      return undefined;
    }
    boundaryOverlaysRef.current = boundaryPaths.map(
      (path) =>
        new google.maps.Polygon({
          paths: path,
          map,
          fillOpacity: 0.05,
          strokeColor: '#1677ff',
          strokeWeight: 2,
          clickable: false,
        }),
    );
    return () => {
      boundaryOverlaysRef.current.forEach((polygon) => polygon.setMap(null));
      boundaryOverlaysRef.current = [];
    };
  }, [map, boundaryPaths]);

  /** 同邮编活跃围栏（生效=实线绿 / 待审=虚线品红，色盲友好双编码） */
  const fenceOverlaysRef = useRef<(google.maps.Polygon | google.maps.Polyline)[]>([]);
  useEffect(() => {
    if (!map) {
      return undefined;
    }
    const overlays: (google.maps.Polygon | google.maps.Polyline)[] = [];
    overlayFences.forEach((fence) => {
      const effective = fence.status === 'EFFECTIVE';
      const color = effective ? '#52c41a' : '#eb2f96';
      overlays.push(
        new google.maps.Polygon({
          paths: fence.path,
          map,
          fillColor: color,
          fillOpacity: 0.18,
          strokeColor: color,
          strokeWeight: effective ? 2 : 0,
          strokeOpacity: effective ? 1 : 0,
          clickable: false,
        }),
      );
      if (!effective) {
        overlays.push(
          new google.maps.Polyline({
            path: [...fence.path, fence.path[0]],
            map,
            strokeOpacity: 0,
            clickable: false,
            icons: [
              {
                icon: {path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, scale: 2.5},
                offset: '0',
                repeat: '12px',
              },
            ],
          }),
        );
      }
    });
    fenceOverlaysRef.current = overlays;
    return () => {
      fenceOverlaysRef.current.forEach((overlay) => overlay.setMap(null));
      fenceOverlaysRef.current = [];
    };
  }, [map, overlayFences]);

  /** 截断预览（青色实线）：生效形状盖在圈画层之上 */
  const previewOverlayRef = useRef<google.maps.Polygon | null>(null);
  useEffect(() => {
    if (!map) {
      return undefined;
    }
    const path = (previewPoints ?? [])
      .filter((p) => typeof p?.latitude === 'number' && typeof p?.longitude === 'number')
      .map((p) => ({lat: p.latitude, lng: p.longitude}));
    if (path.length >= 3) {
      previewOverlayRef.current = new google.maps.Polygon({
        paths: path,
        map,
        fillColor: '#13c2c2',
        fillOpacity: 0.25,
        strokeColor: '#13c2c2',
        strokeWeight: 3,
        clickable: false,
      });
    }
    return () => {
      previewOverlayRef.current?.setMap(null);
      previewOverlayRef.current = null;
    };
  }, [map, previewPoints]);

  /** 本次圈画（橙色），落点变化只重建这一个多边形 */
  const drawnOverlayRef = useRef<google.maps.Polygon | null>(null);
  useEffect(() => {
    if (!map) {
      return undefined;
    }
    if (drawnPath.length >= 3) {
      drawnOverlayRef.current = new google.maps.Polygon({
        paths: drawnPath,
        map,
        fillColor: '#faad14',
        fillOpacity: 0.28,
        strokeColor: '#faad14',
        strokeWeight: 2,
        clickable: false,
      });
    }
    return () => {
      drawnOverlayRef.current?.setMap(null);
      drawnOverlayRef.current = null;
    };
  }, [map, drawnPath]);

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (readonly || !event.latLng) {
        return;
      }
      if (points.length >= MAX_POINTS) {
        return;
      }
      onPointsChange([...points, {latitude: event.latLng.lat(), longitude: event.latLng.lng()}]);
    },
    [readonly, points, onPointsChange],
  );

  if (loadError) {
    return <Alert type="error" message={t('podExempt.map.loadError')} />;
  }
  if (!isLoaded) {
    return <Spin />;
  }

  return (
    <div>
      {(!readonly || overlayFences.length > 0) && (
        <div className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          {!readonly && (
          <Space size="middle">
            <Button
              size="small"
              disabled={points.length === 0}
              onClick={() => onPointsChange(points.slice(0, -1))}
            >
              {t('podExempt.map.undoPoint')}
            </Button>
            <Button size="small" disabled={points.length === 0} onClick={() => onPointsChange([])}>
              {t('podExempt.map.clear')}
            </Button>
            <span className="text-[13px] text-gray-600">
              {t('podExempt.map.pointCount', {count: points.length})}
            </span>
          </Space>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
            <span className="w-2.5 h-2.5 inline-block rounded-sm" style={{background: '#9254de'}} />
            {t('podExempt.map.legendZipcode')}
            <span className="w-2.5 h-2.5 inline-block rounded-sm ml-3" style={{background: '#faad14'}} />
            {readonly ? t('podExempt.map.legendReport') : t('podExempt.map.legendDrawing')}
            <span className="w-2.5 h-2.5 inline-block rounded-sm ml-3" style={{background: '#52c41a'}} />
            {t('podExempt.map.legendEffective')}
            <span className="w-2.5 h-2.5 inline-block rounded-sm ml-3" style={{background: '#eb2f96'}} />
            {t('podExempt.map.legendPending')}
          </span>
        </div>
      )}
      <Spin spinning={boundaryLoading}>
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={US_CENTER}
          zoom={4}
          onLoad={setMap}
          onClick={handleMapClick}
          options={{streetViewControl: false, mapTypeControl: false, clickableIcons: false}}
        >
          {!readonly &&
            drawnPath.map((point, index) => (
              <Marker
                // eslint-disable-next-line react/no-array-index-key
                key={`vertex-${index}`}
                position={point}
                label={{text: String(index + 1), fontSize: '10px'}}
              />
            ))}
          {(extraMarkers ?? []).map((marker, index) => (
            <Marker
              // eslint-disable-next-line react/no-array-index-key
              key={`extra-${index}`}
              position={{lat: marker.lat, lng: marker.lng}}
              label={marker.label ? {text: marker.label, fontSize: '10px'} : undefined}
              opacity={0.8}
            />
          ))}
        </GoogleMap>
      </Spin>
    </div>
  );
};

export default PodExemptFenceDrawMap;
